import { utils } from 'kb-shared';
import { ReactComponent as VisiblePassword } from 'assets/icons/visible-password.svg';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import TextInput from '../../../components/TextInput/NewTextInput';

const { getPasswordErrors, PASSWORD_ERRORS, isPasswordMatch } = utils;

type Props = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  error: boolean;
  hasConfirmation?: boolean;
  confirmationValue?: string;
  onChangeConfirmation?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  confirmationError?: boolean;
};

const CreatePasswordInput = (props: Props) => {
  const {
    value,
    onChange,
    error,
    hasConfirmation = false,
    confirmationValue = '',
    onChangeConfirmation = () => null,
    confirmationError = false
  } = props;
  const [maskedPassword, setMaskedPassword] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);

  return (
    <>
      <PasswordContainer>
        <TextInput
          name={'password'}
          label={'PASSWORD'}
          value={value}
          placeholder={'Create a password'}
          autoComplete="new-password"
          onChange={event => {
            onChange(event);
            setErrors(getPasswordErrors(event.target.value));
          }}
          type={maskedPassword ? 'password' : 'text'}
          error={error}
        />
        <PasswordMaskToggle onClick={() => setMaskedPassword(!maskedPassword)} />
      </PasswordContainer>

      {hasConfirmation && (
        <PasswordContainer>
          <TextInput
            name={'confirmPassword'}
            label={'CONFIRM PASSWORD'}
            value={confirmationValue}
            placeholder={'Confirm the password'}
            autoComplete="new-password"
            onChange={event => {
              onChangeConfirmation(event);
            }}
            type={maskedPassword ? 'password' : 'text'}
            error={confirmationError}
          />
          <PasswordMaskToggle onClick={() => setMaskedPassword(!maskedPassword)} />
        </PasswordContainer>
      )}

      <PasswordRequirement>
        <p>For security purposes, passwords must contain:</p>
        <ul>
          <PasswordRequirementItem error={errors.includes(PASSWORD_ERRORS.EQUAL_OR_GREATER_THAN)}>
            <YellowBullet /> 8 or more characters
          </PasswordRequirementItem>
          <PasswordRequirementItem
            error={errors.includes(PASSWORD_ERRORS.CONTAINS_NUMERIC_CHARACTERS)}
          >
            <YellowBullet /> numbers
          </PasswordRequirementItem>
          <PasswordRequirementItem
            error={errors.includes(PASSWORD_ERRORS.CONTAINS_UPPER_AND_LOWER_CASE_CHARACTERS)}
          >
            <YellowBullet /> lower & uppercase letters
          </PasswordRequirementItem>
          <PasswordRequirementItem
            error={errors.includes(PASSWORD_ERRORS.CONTAINS_SPECIAL_CHARACTERS)}
          >
            <YellowBullet /> special characters (e.g. ! @ # $ %)
          </PasswordRequirementItem>
          {hasConfirmation && (
            <PasswordRequirementItem error={!isPasswordMatch(value, confirmationValue)}>
              <YellowBullet /> passwords must match
            </PasswordRequirementItem>
          )}
        </ul>
      </PasswordRequirement>
    </>
  );
};

const PasswordContainer = styled.div`
  position: relative;
`;

const PasswordRequirement = styled.div`
  ${props => props.theme.fonts.FoundersGrotesk};
  font-size: 12px;
  font-style: italic;

  p,
  ul {
    margin: 0;
  }

  ul {
    list-style: none;
    padding: 0;
  }
`;

interface PasswordRequirementItemProps {
  error: boolean;
}

const PasswordRequirementItem = styled.li`
  ${(props: PasswordRequirementItemProps) =>
    props.error &&
    css`
      color: red;
    `}
`;

const PasswordMaskToggle = styled(VisiblePassword)`
  cursor: pointer;
  position: absolute;
  right: 10px;
  bottom: 24px;
`;

const YellowBullet = () => {
  return <YellowBulletWrapper>●</YellowBulletWrapper>;
};

const YellowBulletWrapper = styled.span`
  font-size: 12px;
  color: ${props => props.theme.colors.KBYellow};
  margin-right: 5px;
`;

export default CreatePasswordInput;
